import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const InvoiceDetailModal = ({ isOpen, onClose, invoice, onAddPayment, onSendInvoice, onRefresh }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const getEffectiveStatus = () => {
    return invoice?.invoice_status || invoice?.status?.name || 'draft';
  };

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return { color: 'bg-muted text-muted-foreground border-border', icon: 'FileEdit' };
      case 'sent': return { color: 'bg-primary/10 text-primary border-primary/20', icon: 'Send' };
      case 'pending': return { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock' };
      case 'partial': return { color: 'bg-blue-500/10 text-blue-600 border-blue-200', icon: 'PieChart' };
      case 'paid': return { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle' };
      case 'overdue': return { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle' };
      case 'cancelled': return { color: 'bg-muted text-muted-foreground border-border', icon: 'XCircle' };
      default: return { color: 'bg-muted text-muted-foreground border-border', icon: 'Circle' };
    }
  };

  const effectiveStatus = getEffectiveStatus();
  const statusCfg = getStatusConfig(effectiveStatus);

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await supabase?.from('invoices')?.update({
        invoice_status: newStatus,
        updated_at: new Date()?.toISOString(),
        ...(newStatus === 'paid' ? { paid_date: new Date()?.toISOString() } : {}),
      })?.eq('id', invoice?.id);
      setStatusMsg(`Status updated to ${newStatus}`);
      setTimeout(() => setStatusMsg(''), 3000);
      onRefresh?.();
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const isOverdue = invoice?.due_date && new Date(invoice?.due_date) < new Date() &&
    !['paid', 'cancelled']?.includes(effectiveStatus?.toLowerCase());

  const daysOverdue = isOverdue
    ? Math.floor((new Date() - new Date(invoice?.due_date)) / (1000 * 60 * 60 * 24))
    : 0;

  const statusTransitions = {
    draft: ['sent', 'cancelled'],
    sent: ['paid', 'overdue', 'cancelled'],
    pending: ['sent', 'paid', 'overdue', 'cancelled'],
    partial: ['paid', 'overdue', 'cancelled'],
    overdue: ['paid', 'cancelled'],
    paid: [],
    cancelled: ['draft'],
  };

  const availableTransitions = statusTransitions?.[effectiveStatus?.toLowerCase()] || [];

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Invoice Details</h2>
              <p className="text-sm text-muted-foreground">{invoice?.invoice_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSendInvoice?.(invoice)}
                iconName="Send"
                iconPosition="left"
              >
                Send Invoice
              </Button>
            )}
            {effectiveStatus !== 'paid' && effectiveStatus !== 'cancelled' && (
              <Button
                size="sm"
                onClick={() => onAddPayment?.(invoice)}
                iconName="DollarSign"
                iconPosition="left"
              >
                Record Payment
              </Button>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
              <Icon name="X" size={20} color="var(--color-foreground)" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {statusMsg && (
            <div className="p-3 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
              <Icon name="CheckCircle" size={16} color="var(--color-success)" />
              <p className="text-sm text-success">{statusMsg}</p>
            </div>
          )}

          {isOverdue && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
              <p className="text-sm text-error font-medium">This invoice is {daysOverdue} days overdue</p>
            </div>
          )}

          {/* Status & Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Customer</h3>
              <p className="font-medium text-foreground">{invoice?.customer?.name}</p>
              <p className="text-sm text-muted-foreground">{invoice?.customer?.email}</p>
              <p className="text-sm text-muted-foreground">{invoice?.customer?.phone}</p>
            </div>
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Status</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${statusCfg?.color}`}>
                    <Icon name={statusCfg?.icon} size={13} />
                    {effectiveStatus?.charAt(0)?.toUpperCase() + effectiveStatus?.slice(1)}
                  </span>
                  {availableTransitions?.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {availableTransitions?.map(transition => (
                        <button
                          key={transition}
                          onClick={() => handleStatusChange(transition)}
                          disabled={updatingStatus}
                          className="px-2.5 py-1 text-xs border border-border rounded-full hover:bg-muted transition-smooth disabled:opacity-50"
                        >
                          → {transition?.charAt(0)?.toUpperCase() + transition?.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {invoice?.sent_at && (
                <div>
                  <h3 className="text-sm font-caption font-medium text-muted-foreground mb-1">Sent</h3>
                  <p className="text-sm text-foreground flex items-center gap-1">
                    <Icon name="Send" size={12} className="text-muted-foreground" />
                    {formatDate(invoice?.sent_at)}
                    {invoice?.sent_to_email && <span className="text-muted-foreground">→ {invoice?.sent_to_email}</span>}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Invoice Date</h3>
              <p className="font-medium text-foreground">{formatDate(invoice?.created_at)}</p>
            </div>
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Due Date</h3>
              <p className={`font-medium ${isOverdue ? 'text-error' : 'text-foreground'}`}>{formatDate(invoice?.due_date)}</p>
            </div>
            {invoice?.paid_date && (
              <div>
                <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Paid Date</h3>
                <p className="font-medium text-success">{formatDate(invoice?.paid_date)}</p>
              </div>
            )}
          </div>

          {/* Invoice Items */}
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Invoice Items</h3>
            <div className="bg-background rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Qty</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Unit Price</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Discount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice?.invoice_items?.map((item) => (
                    <tr key={item?.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-sm text-foreground">{item?.description}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{item?.quantity}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{formatCurrency(item?.unit_price)}</td>
                      <td className="px-4 py-3 text-sm text-foreground">{item?.discount_percent}%</td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{formatCurrency(item?.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-6">
            <div className="max-w-md ml-auto space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium text-foreground">{formatCurrency(invoice?.subtotal)}</span>
              </div>
              {parseFloat(invoice?.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount:</span>
                  <span className="font-medium text-error">-{formatCurrency(invoice?.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax ({invoice?.tax_rate}%):</span>
                <span className="font-medium text-foreground">{formatCurrency(invoice?.tax_amount)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-border pt-3">
                <span className="text-foreground">Total Amount:</span>
                <span className="text-primary">{formatCurrency(invoice?.total_amount)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="font-medium text-success">{formatCurrency(invoice?.amount_paid)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-border pt-3">
                <span className="text-foreground">Amount Due:</span>
                <span className={parseFloat(invoice?.amount_due || 0) > 0 ? 'text-error' : 'text-success'}>
                  {formatCurrency(invoice?.amount_due)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment History */}
          {invoice?.payments?.length > 0 && (
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Payment History</h3>
              <div className="bg-background rounded-lg border border-border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Payment #</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Method</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Transaction ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice?.payments?.map((payment) => (
                      <tr key={payment?.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-sm text-foreground">{payment?.payment_number}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{formatDate(payment?.payment_date)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-sm text-foreground">
                            <Icon name="CreditCard" size={12} className="text-muted-foreground" />
                            {payment?.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-success">{formatCurrency(payment?.amount)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{payment?.transaction_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {invoice?.notes && (
            <div>
              <h3 className="text-sm font-caption font-medium text-muted-foreground mb-2">Notes</h3>
              <p className="text-sm text-foreground bg-muted/30 rounded-lg p-4">{invoice?.notes}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-end">
          <Button variant="outline" onClick={onClose} iconName="X" iconPosition="left">Close</Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailModal;